import { DndContext } from '@dnd-kit/core';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useGoalTodayReorder } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { TaskCard } from 'shared/components/connected/task-card';
import { OverlayComponentWrapper } from 'shared/components/ui/overlay-component-wrapper';
import { SortableOverlay } from 'shared/components/ui/sortable-overlay';
import {
  Headers,
  TaskCardTaskSection,
} from 'shared/components/ui/task-card-task-section';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useActiveTaskId } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useDroppableSectionControls } from 'shared/hooks/use-droppable-section-controls';
import { useMouseOutside } from 'shared/hooks/use-mouse-outside';
import { useSectionCollisionDetection } from 'shared/hooks/use-section-collision-detection';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';
import { useUser } from 'shared/hooks/use-user';
import { goalInboxID } from 'shared/types/orderable-section';
import { DueDisplayOptions } from 'shared/types/task-base';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { noCollisionDetection } from 'shared/utils/no-collision-detection';

import { useFullCalendarDraggableTasks } from '../../hooks/use-full-calendar-draggable-tasks';
import { useGoalTaskSections } from '../../hooks/use-goal-task-sections';
import * as Styled from './goal-grouping-tasks-list.style';

const displayDue = [DueDisplayOptions.Past, DueDisplayOptions.Future];

export const GoalGroupingTasksList: React.FC = () => {
  const today = useToday();
  const user = useUser();
  const containerRef = useRef<HTMLDivElement>(null);

  const openPremiumDialog = useOpenPremiumDialog();

  const { taskCardTasks, sections: initialSections } = useGoalTaskSections();
  const onDragEnd = useGoalTodayReorder(initialSections);

  const {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    activeId,
    sections,
    setSections,
  } = useDroppableSectionControls({ initialSections, onDragEnd });
  const sensors = useSortableSensors();
  const collisionStrategy = useSectionCollisionDetection(activeId, sections);

  const selectedTask = useActiveTaskId();

  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const activeItem = useMemo(
    () =>
      activeId ? taskCardTasks.find(({ id }) => id === activeId) : undefined,
    [activeId, taskCardTasks],
  );

  const tasks = useMemo(
    () => taskCardTasks.map(({ raw }) => raw.task),
    [taskCardTasks],
  );
  useFullCalendarDraggableTasks(tasks, containerRef);

  const onMouseLeave = useCallback(
    () => setSections(initialSections),
    [initialSections, setSections],
  );

  const isOutsideList = useMouseOutside(containerRef, {
    enabled: !!activeId,
    onMouseLeave,
  });

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={
        isOutsideList ? noCollisionDetection : collisionStrategy
      }
    >
      <Styled.Container ref={containerRef}>
        {sections.map(({ id, items }) => (
          <Styled.Section key={id}>
            <TaskCardTaskSection
              droppableId={id}
              goals={goals}
              lifeAreas={lifeAreas}
              tasks={items}
              selectedTask={selectedTask}
              date={today}
              goalId={id !== goalInboxID ? id : undefined}
              tagIds={user?.settings?.todayFilterByTags}
              timeFormat={getTimeFormat(user)}
              displayDue={displayDue}
              headerToDisplay={Headers.Goal}
              onPremium={openPremiumDialog}
              hideGoal
              headerIsLink
              canCollapse
              canCreateTask
              canReorder
            />
          </Styled.Section>
        ))}
      </Styled.Container>

      <SortableOverlay>
        {!!activeItem && (
          <OverlayComponentWrapper isOutside={isOutsideList}>
            <TaskCard
              task={activeItem.raw.task}
              displayDue={displayDue}
              isSelected={selectedTask === activeItem.id}
              hideGoal
            />
          </OverlayComponentWrapper>
        )}
      </SortableOverlay>
    </DndContext>
  );
};
